import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import SEO from '../../../components/SEO';

const cities = [
  {
    name: 'Toronto',
    slug: 'toronto',
    businesses: '200+',
    tagline: 'Financial District to Liberty Village',
    services: ['4K Cameras', 'Access Control', '24/7 Monitoring']
  },
  {
    name: 'Mississauga',
    slug: 'mississauga',
    businesses: '150+',
    tagline: 'Airport Corporate to Square One',
    services: ['Video Analytics', 'Alarm Systems', 'Cloud Storage']
  },
  {
    name: 'Brampton',
    slug: 'brampton',
    businesses: '80+',
    tagline: 'Industrial Parks to Retail Centers',
    services: ['Perimeter Security', 'Access Control', 'Live Monitoring']
  },
  {
    name: 'Oakville',
    slug: 'oakville',
    businesses: '60+',
    tagline: 'Corporate Offices to Healthcare',
    services: ['IP Cameras', 'Intrusion Detection', 'Video Verification']
  },
  {
    name: 'Hamilton',
    slug: 'hamilton',
    businesses: '70+',
    tagline: 'Manufacturing to Downtown Core',
    services: ['Industrial Security', 'Access Control', 'Remote Monitoring']
  },
  {
    name: 'Richmond Hill',
    slug: 'richmond-hill',
    businesses: '55+',
    tagline: 'Business Parks to Tech Corridors',
    services: ['Smart Cameras', 'Keycard Systems', 'Cloud Management']
  },
  {
    name: 'Vaughan',
    slug: 'vaughan',
    businesses: '90+',
    tagline: 'Vaughan Corporate Centre to Concord',
    services: ['HD Surveillance', 'Biometric Access', '24/7 Support']
  },
  {
    name: 'Whitby',
    slug: 'whitby',
    businesses: '40+',
    tagline: 'Port Whitby to Business Districts',
    services: ['Commercial Alarms', 'CCTV Systems', 'Mobile Monitoring']
  },
  {
    name: 'Ajax',
    slug: 'ajax',
    businesses: '35+',
    tagline: 'Industrial Zones to Retail Plazas',
    services: ['Warehouse Security', 'Access Control', 'Video Storage']
  },
  {
    name: 'Oshawa',
    slug: 'oshawa',
    businesses: '45+',
    tagline: 'Auto Sector to Commercial Corridors',
    services: ['Industrial Cameras', 'Alarm Monitoring', 'Remote Access']
  }
];

const trustPoints = [
  {
    icon: 'ri-map-pin-line',
    title: 'Local Response Teams',
    description: 'Technicians in every GTA region for same-day emergency service and faster installation timelines'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'City-Specific Compliance',
    description: 'We understand Toronto bylaws, Hamilton fire codes, and regional alarm permit requirements'
  },
  {
    icon: 'ri-building-line',
    title: 'Industry Expertise',
    description: 'From Mississauga warehouses to Toronto financial offices — we know your sector\'s security needs'
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'GTA-Wide Monitoring',
    description: 'ULC-certified monitoring station with direct connections to local police and fire departments'
  }
];

export default function CommercialSecurityServiceAreas() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Commercial Security Systems Across the GTA',
    description: 'Professional commercial security systems, business alarms, and access control across Toronto, Mississauga, Brampton, and all GTA cities. 500+ businesses protected.',
    url: 'https://alliancesecuritysystems.com/commercial-security/service-areas',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://alliancesecuritysystems.com'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Commercial Security',
          item: 'https://alliancesecuritysystems.com/commercial-security'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Service Areas',
          item: 'https://alliancesecuritysystems.com/commercial-security/service-areas'
        }
      ]
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: cities.map((city, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Service',
          name: `Commercial Security ${city.name}`,
          url: `https://alliancesecuritysystems.com/commercial-security/${city.slug}`,
          areaServed: {
            '@type': 'City',
            name: city.name
          }
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Commercial Security Systems Across the GTA | Business Alarms & Cameras"
        description="Professional commercial security systems across Toronto, Mississauga, Brampton, Oakville, Hamilton, and all GTA cities. 500+ businesses protected. Free security assessment."
        keywords="commercial security GTA, business security systems Toronto, commercial alarm systems Mississauga, business cameras Brampton"
        canonical="/commercial-security/service-areas"
        structuredData={structuredData}
      />

      <main className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <nav className="bg-gray-50 border-b border-gray-200" itemScope itemType="https://schema.org/BreadcrumbList">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <ol className="flex items-center gap-2 text-sm">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <span className="text-gray-400">/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/commercial-security" className="text-gray-600 hover:text-teal-600 transition-colors" itemProp="item">
                  <span itemProp="name">Commercial Security</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <span className="text-gray-400">/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-gray-900 font-medium" itemProp="name">Service Areas</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 text-white py-20">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/6bed32e88a052596bb4f12e9fd31af31.jpg')] bg-cover bg-center opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Commercial Security Systems<br />Across the GTA
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Professional business security solutions in Toronto, Mississauga, Brampton, and all Greater Toronto Area cities. Local teams, 24/7 monitoring, and industry-leading technology.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Get Free Assessment
              </Link>
              <Link
                to="/commercial-security"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold transition-all border border-white/30 whitespace-nowrap"
              >
                View All Solutions
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-teal-600 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-teal-100 text-sm">Commercial Clients</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">10</div>
                <div className="text-teal-100 text-sm">GTA Cities Served</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">25+</div>
                <div className="text-teal-100 text-sm">Years Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">20%</div>
                <div className="text-teal-100 text-sm">Avg Insurance Discount</div>
              </div>
            </div>
          </div>
        </section>

        {/* City Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Choose Your City
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Select your location to view commercial security solutions, local case studies, and city-specific pricing for your business.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cities.map((city) => (
                <Link
                  key={city.slug}
                  to={`/commercial-security/${city.slug}`}
                  className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-teal-500"
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors mb-2">
                          {city.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-1">{city.tagline}</p>
                        <p className="text-sm font-semibold text-teal-600">{city.businesses} Businesses Protected</p>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-600 transition-colors">
                        <i className="ri-building-line text-xl text-teal-600 group-hover:text-white transition-colors"></i>
                      </div>
                    </div>

                    <div className="space-y-2 mb-6">
                      {city.services.map((service, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-teal-600"></i>
                          <span>{service}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 text-teal-600 font-semibold group-hover:gap-3 transition-all">
                      <span>View {city.name} Solutions</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Local Matters */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Local Commercial Security Matters
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Working with a local commercial security provider means faster response times, better compliance knowledge, and technicians who understand your city's unique business landscape.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {trustPoints.map((point, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-xl mx-auto mb-6">
                    <i className={`${point.icon} text-3xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Protect Your Business?
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              Get a free commercial security assessment for your GTA location. No obligation, no pressure — just expert advice tailored to your business.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Schedule Free Assessment
              </Link>
              <Link
                to="/commercial-security"
                className="bg-teal-800 hover:bg-teal-900 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap"
              >
                View All Commercial Solutions
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
